import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Scale,
  FileText,
  AlertTriangle,
  Wallet,
  Shield,
  HandshakeIcon,
  MessageCircle,
  CheckCircle2,
  ChevronDown,
  HelpCircle,
} from "lucide-react";

const LOGO_ICON = "https://customer-assets.emergentagent.com/job_direito-trabalho-sp/artifacts/8b5v8061_IconOnly_Transparent_NoBuffer.png";
const WHATSAPP_LINK = "https://wa.me/5511917611575?text=Olá! Gostaria de uma consulta sobre Direito Cível.";

const CivelPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      pergunta: "Qual a diferença entre dano moral e dano material?",
      resposta: "Dano material é o prejuízo financeiro comprovável (despesas médicas, lucros cessantes, conserto de bens). Dano moral é a lesão a direitos da personalidade (honra, imagem, dignidade) que causa sofrimento psicológico — não exige comprovação de gastos, mas sim do ato ilícito e do abalo sofrido."
    },
    {
      pergunta: "Quanto tempo tenho para cobrar uma dívida?",
      resposta: "Depende da natureza da dívida. Cheques prescrevem em 6 meses. Aluguéis e prestações de serviços em 3 anos. Contratos em geral em 10 anos. Após esse prazo, a dívida continua existindo, mas perde a força executiva judicial."
    },
    {
      pergunta: "Posso processar alguém por negativar meu nome indevidamente?",
      resposta: "Sim. A inscrição indevida em cadastros de proteção ao crédito (SPC/Serasa) gera dano moral presumido (in re ipsa). Você pode pedir indenização e a retirada imediata do apontamento. É importante guardar comprovantes de pagamento da dívida."
    },
    {
      pergunta: "O que é responsabilidade civil objetiva?",
      resposta: "É quando o responsável deve indenizar independentemente de culpa. Aplica-se, por exemplo, nas relações de consumo, acidentes de trânsito causados por empresas de transporte, e danos ambientais. Basta comprovar o dano e o nexo causal."
    },
    {
      pergunta: "Como funciona uma ação de cobrança?",
      resposta: "Se você tem um crédito (cheque, contrato, nota promissória), pode ingressar com ação de execução (mais rápida, se tiver título executivo) ou ação de cobrança (processo de conhecimento). O devedor é citado para pagar, e seus bens podem ser penhorados."
    },
    {
      pergunta: "Posso resolver um conflito sem ir à Justiça?",
      resposta: "Sim. A mediação e a arbitragem são formas alternativas de resolução de conflitos. A mediação busca o acordo entre as partes com ajuda de um mediador. A arbitragem é uma decisão vinculante proferida por um árbitro. Ambas são mais rápidas e sigilosas que processos judiciais."
    },
  ];

  const servicos = [
    { icon: <AlertTriangle className="w-5 h-5" />, title: "Indenizações", desc: "Danos morais, materiais e estéticos" },
    { icon: <Shield className="w-5 h-5" />, title: "Responsabilidade Civil", desc: "Reparação de danos causados por terceiros" },
    { icon: <Wallet className="w-5 h-5" />, title: "Cobranças", desc: "Recuperação de créditos e dívidas" },
    { icon: <Scale className="w-5 h-5" />, title: "Execução de Títulos", desc: "Execução de cheques, notas e contratos" },
    { icon: <FileText className="w-5 h-5" />, title: "Contratos", desc: "Elaboração, análise e revisão" },
    { icon: <HandshakeIcon className="w-5 h-5" />, title: "Negociação", desc: "Mediação e resolução de conflitos" },
  ];

  return (
    <>
      <Helmet>
        <title>Advogado Cível | Indenizações e Contratos</title>
        <meta name="description" content="Advogado cível especializado. Atuação em indenizações, responsabilidade civil, cobranças, execuções e contratos." />
        <meta name="keywords" content="advogado cível, indenização, danos morais, responsabilidade civil, contratos" />
      </Helmet>

      <div>
        {/* Hero */}
        <section className="pt-32 pb-20 bg-gradient-to-br from-slate-50 to-white relative overflow-hidden">
          <div className="absolute right-0 top-1/2 -translate-y-1/2 opacity-[0.03] pointer-events-none">
            <img src={LOGO_ICON} alt="" className="w-[500px] h-auto" style={{ filter: "brightness(0)" }} />
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <p className="text-slate-700 font-medium tracking-wider text-sm mb-4">DIREITO CÍVEL</p>
            <h1
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 max-w-3xl"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Advogado <span className="text-slate-700">Cível</span>
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mb-8">
              Atuação em demandas indenizatórias, responsabilidade civil, cobranças, 
              execução de títulos e elaboração de contratos.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-slate-700 hover:bg-slate-800 text-white px-8 py-4 rounded font-medium transition-all">
                <MessageCircle className="w-5 h-5" />
                Fale com Especialista
              </a>
              <Link to="/contato"
                className="inline-flex items-center justify-center border-2 border-gray-300 hover:border-slate-700 text-gray-700 hover:text-slate-700 px-8 py-4 rounded font-medium transition-all">
                Enviar Mensagem
              </Link>
            </div>
          </div>
        </section>

        {/* Quando procurar */}
        <section className="py-16 bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Quando Procurar um Advogado Cível?
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Quando sofreu um dano e precisa de indenização, precisa cobrar uma dívida, 
                vai assinar um contrato importante, foi vítima de propaganda enganosa ou 
                teve problemas com produtos ou serviços.
              </p>
            </div>
          </div>
        </section>

        {/* Serviços */}
        <section className="py-20 bg-gray-50 relative overflow-hidden">
          <div className="absolute left-0 bottom-0 opacity-[0.02] pointer-events-none">
            <img src={LOGO_ICON} alt="" className="w-[400px] h-auto" style={{ filter: "brightness(0)" }} />
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-10" style={{ fontFamily: "'Playfair Display', serif" }}>
              Nossos Serviços Cíveis
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {servicos.map((servico, index) => (
                <div key={index} className="bg-white p-6 rounded-lg border border-gray-100 hover:shadow-md transition-all">
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center text-slate-700 mb-4">
                    {servico.icon}
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">{servico.title}</h4>
                  <p className="text-gray-600 text-sm">{servico.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Diferencial */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Análise Personalizada do Seu Caso
                </h2>
                <p className="text-gray-600 mb-6">
                  Cada caso é único. Analisamos sua situação com cuidado para encontrar a melhor 
                  estratégia jurídica, seja por via judicial ou através de negociação.
                </p>
                <div className="space-y-4">
                  {[
                    "Avaliação detalhada da situação",
                    "Orientação sobre direitos e possibilidades",
                    "Estratégia processual personalizada",
                    "Busca por soluções consensuais",
                    "Atuação em todas as instâncias",
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-slate-700 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-slate-100 p-8 rounded-xl">
                <Scale className="w-10 h-10 text-slate-700 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Resolução de Conflitos</h3>
                <p className="text-gray-600 mb-6">
                  Buscamos sempre a melhor solução para seu caso, seja por meio de negociação, 
                  mediação ou atuação judicial quando necessário.
                </p>
                <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-slate-700 hover:bg-slate-800 text-white px-6 py-3 rounded font-medium transition-all w-full">
                  <MessageCircle className="w-5 h-5" />
                  Consultar Advogado
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 bg-gray-50 relative overflow-hidden">
          <div className="absolute right-0 bottom-0 opacity-[0.02] pointer-events-none">
            <img src={LOGO_ICON} alt="" className="w-[400px] h-auto" style={{ filter: "brightness(0)" }} />
          </div>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center mb-12">
              <HelpCircle className="w-10 h-10 text-slate-700 mx-auto mb-4" />
              <h2 
                className="text-2xl md:text-3xl font-bold text-gray-900 mb-4"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Perguntas Frequentes
              </h2>
              <p className="text-gray-600">
                Tire suas principais dúvidas sobre Direito Cível
              </p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div 
                  key={index}
                  className="border border-gray-200 rounded-lg overflow-hidden bg-white"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-medium text-gray-900 pr-4">{faq.pergunta}</span>
                    <ChevronDown 
                      className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform ${
                        openFaq === index ? "rotate-180" : ""
                      }`} 
                    />
                  </button>
                  {openFaq === index && (
                    <div className="px-5 pb-5 pt-0">
                      <p className="text-gray-600 leading-relaxed">{faq.resposta}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-slate-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Precisa de Assessoria Cível?
            </h2>
            <p className="text-slate-300 mb-8 max-w-xl mx-auto">
              Entre em contato e resolva suas questões cíveis com quem entende do assunto.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded font-medium transition-all">
                <MessageCircle className="w-5 h-5" />
                Falar pelo WhatsApp
              </a>
              <Link to="/contato"
                className="inline-flex items-center justify-center border-2 border-white/30 hover:border-white text-white px-8 py-4 rounded font-medium transition-all">
                Enviar Mensagem
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default CivelPage;
